import { KeyboardArrowDown } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@mdi/react';
import { mdiCheck } from '@mdi/js';
import { declareVariants } from '../../lib/variantMatch';
import { cn } from '../../lib/cn';

export interface SelectOption {
	value: string;
	label: string;
	icon?: React.ReactNode;
	triggerIcon?: React.ReactNode;
	color?: string;
}

type SelectVariant = 'default' | 'borderless';
type SelectSize = 'md' | 'md2' | 'sm';

interface SelectProps {
	/** Currently selected option value. Match an `option.value` to render that option's label. */
	value: string;
	/** Called with the new option's `value` when the user picks an option. */
	onChange: (value: string) => void;
	/** Options to render in the dropdown. */
	options: SelectOption[];
	/** Trigger text shown when no option matches `value`. Defaults to "Pick an option". */
	placeholder?: string;
	/** Floating label drawn above the trigger's top border. */
	label?: string;
	/** Leading icon shown in the trigger. Falls back to the selected option's `icon` when omitted. */
	icon?: React.ReactNode;
	/** When true, draws a vertical divider after `icon` to suggest it's interactive (paired with a parent click handler). */
	iconIsButton?: boolean;
	/** Trailing icon that replaces the default chevron. Per-option `triggerIcon` takes precedence. */
	triggerIcon?: React.ReactNode;
	/** Force the trigger icon to rotate 180° when open (default: only the chevron rotates). */
	triggerRotate?: boolean;
	/** Hide the trigger label, leaving only `icon` + chevron. Used for icon-only pickers. */
	compact?: boolean;
	/** Override trigger background to transparent (useful inside hover/highlight rows). */
	transparent?: boolean;
	/** Permanent pressed-border state, used to indicate the value differs from default. */
	highlighted?: boolean;
	/** Extra classes applied to the root wrapper. */
	className?: string;
	/** Disable interaction. The trigger gets `cursor-not-allowed` + reduced opacity. */
	disabled?: boolean;
	/** Stretch root + trigger to fill the parent. Default is fit-content. */
	fullWidth?: boolean;
	/** Show a search input above the option list and filter options by label. */
	searchable?: boolean;
	/** Render the dropdown in-flow (relative) instead of as an absolute overlay. */
	inline?: boolean;
	/** Visual style: `default` (bordered) or `borderless` (ghost). */
	variant?: SelectVariant;
	/** Trigger height: `md` = 36px (default), `sm` = 28px. */
	size?: SelectSize;
	/**
	 * Let the trigger shrink to the current value's width instead of reserving space for the
	 * widest option. Causes the trigger to reflow when the value changes; off by default.
	 */
	shrinkToValue?: boolean;
}

const iconSlot = cn(
	'flex size-5 shrink-0 items-center justify-center',
	'[&>svg]:block [&>svg]:size-5! [&>svg]:max-h-5 [&>svg]:max-w-5 [&>svg]:text-[20px] [&>svg]:leading-none',
);

export function Select({
	value,
	onChange,
	options,
	placeholder = 'Pick an option',
	label,
	icon,
	iconIsButton = false,
	triggerIcon,
	triggerRotate = false,
	compact = false,
	transparent = false,
	highlighted = false,
	className = '',
	disabled = false,
	fullWidth = false,
	searchable = false,
	inline = false,
	variant = 'default',
	size = 'md',
	shrinkToValue = false,
}: SelectProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [search, setSearch] = useState('');
	const selectRef = useRef<HTMLDivElement>(null);
	const listboxRef = useRef<HTMLDivElement>(null);
	const optionRefs = useRef<(HTMLButtonElement)[]>([]);

	const hasIcons = icon || options.some((option) => option.icon);
	const widestOption: SelectOption = options.reduce(
		(max, option) => (option.label.length > max.label.length ? option : max),
		options[0],
	);

	const selectedOption = options.find((opt) => opt.value === value);
	const hasCustomTriggerIcon = Boolean(selectedOption?.triggerIcon || triggerIcon);
	const chevronRotate = Boolean(
		isOpen && ((!selectedOption?.triggerIcon && !triggerIcon) || triggerRotate),
	);

	const variants = declareVariants({
		variant,
		size,
		isOpen,
		fullWidth,
		inline,
		compact,
		transparent,
		highlighted,
		disabled,
		hasCustomTriggerIcon,
		chevronRotate,
		isPlaceholder: !selectedOption,
	});

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			setSearch('');
		}
	}, [isOpen]);

	useEffect(() => {
		const el = optionRefs.current[activeIndex];
		if (el && el.scrollIntoView) {
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [activeIndex]);

	const filteredOptions = () => (searchable && search
		? options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()))
		: options);

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (disabled) return;
		switch (event.key) {
			case 'Enter':
			case ' ':
				event.preventDefault();
				if (!isOpen) {
					setIsOpen(true);
				} else if (activeIndex !== -1) {
					const filtered = filteredOptions();
					onChange(filtered[activeIndex].value);
					setIsOpen(false);
				}
				break;
			case 'Escape':
				setIsOpen(false);
				break;
			case 'ArrowDown':
				event.preventDefault();
				if (!isOpen) {
					setIsOpen(true);
				} else {
					setActiveIndex((prev) => {
						const filtered = filteredOptions();
						return Math.min(prev + 1, filtered.length - 1);
					});
				}
				break;
			case 'ArrowUp':
				event.preventDefault();
				if (!isOpen) {
					setIsOpen(true);
				} else {
					setActiveIndex((prev) => Math.max(prev - 1, 0));
				}
				break;
			default:
				// pass-through
		}
	};

	return (
		<div
			ref={selectRef}
			className={cn(
				'relative w-fit',
				variants({ fullWidth: 'w-full' }),
				className,
			)}
		>
			{label && (
				<div className="absolute -top-[0.8em] left-2 z-0 px-1">
					<span className="text-xs text-grey-800">{label}</span>
					<div className="absolute inset-0 top-[0.8em] -z-[1] bg-bg-static-surface_default" />
				</div>
			)}

			<button
				type="button"
				role="combobox"
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls="select-dropdown"
				aria-label={label || placeholder}
				disabled={disabled}
				className={cn(
					'box-border flex w-fit items-center justify-between gap-2 rounded-md',
					'text-sm leading-5 text-text-primary',
					'disabled:cursor-not-allowed disabled:opacity-50',
					variants({
						variant: {
							default: 'border border-borders-primary bg-bg-static-surface_default enabled:hover:border-borders-primary_hover',
							borderless: cn(
								'border border-transparent bg-bg-static-surface_default font-normal tracking-[0.14px]',
								'enabled:hover:border-transparent enabled:hover:bg-bg-interactive-light-bg-active_hover',
							),
						},
						size: {
							md: 'h-9 py-2 pl-3 pr-4',
							md2: 'h-10 py-2.5 pl-3 pr-2',
							sm: 'h-7 py-1 pl-2 pr-2',
						},
						isOpen: 'bg-grey-70',
						transparent: 'bg-transparent',
						fullWidth: 'w-full',
						when: [
							[{ variant: 'default', isOpen: true }, 'border-borders-primary_pressed!'],
							[{ variant: 'borderless', isOpen: true }, 'border-transparent'],
							[{ variant: 'default', highlighted: true }, 'border-borders-primary_pressed!'],
						],
					}),
				)}
				onClick={() => !disabled && setIsOpen(!isOpen)}
				onKeyDown={handleKeyDown}
				onBlur={(e) => {
					if (!selectRef.current?.contains(e.relatedTarget)) {
						setIsOpen(false);
					}
				}}
			>
				<div
					className={cn(
						'z-[1] flex min-w-0 flex-1 flex-col',
						variants({ fullWidth: 'block w-full' }),
					)}
				>
					<div className="flex min-w-0 items-center gap-2">
						{(icon || selectedOption?.icon) && (
							<>
								<div className={iconSlot}>
									{icon ?? selectedOption?.icon}
								</div>
								{(!compact && iconIsButton) && (
									<div className="mr-1 h-5 w-px shrink-0 bg-borders-primary" />
								)}
							</>
						)}
						{!compact && (
							<span
								className={cn(
									'min-w-0 flex-1 truncate text-left',
									variants({
										isPlaceholder: 'text-grey-250',
										fullWidth: 'block w-full',
									}),
								)}
							>
								{selectedOption?.label || placeholder}
							</span>
						)}
					</div>
					{/* Reserves space for the widest option so the trigger doesn't reflow on value change. */}
					{!shrinkToValue && (
						<div className="invisible flex h-0 items-center gap-2" aria-hidden="true">
							{hasIcons && (
								<div className={iconSlot}>
									{icon || widestOption?.icon}
								</div>
							)}
							{!compact && <span className="truncate">{widestOption?.label}</span>}
						</div>
					)}
				</div>
				<div
					className={cn(
						'flex size-5 shrink-0 items-center justify-center transition-transform text-fg-primary',
						'[&>svg]:block [&>svg]:size-5! [&>svg]:max-h-5 [&>svg]:max-w-5 [&>svg]:text-[20px] [&>svg]:leading-none',
						variants({
							variant: { default: undefined, borderless: 'rounded-sm' },
							size: {
								md: undefined,
								md2: undefined,
								sm: 'size-4 [&>svg]:size-4! [&>svg]:max-h-4 [&>svg]:max-w-4 [&>svg]:text-[16px]',
							},
							disabled: 'text-fg-primary_disabled',
							hasCustomTriggerIcon: 'text-fg-primary_inactive',
							chevronRotate: 'rotate-180',
						}),
					)}
				>
					{selectedOption?.triggerIcon ?? triggerIcon ?? <KeyboardArrowDown />}
				</div>
			</button>

			{isOpen && !disabled && (
				<div
					id="select-dropdown"
					role="listbox"
					ref={listboxRef}
					className={cn(
						'mt-1 flex max-h-60 min-w-full w-max max-w-[calc(100vw-2rem)] flex-col items-start gap-1 overflow-y-auto',
						'rounded-md border border-grey-100 bg-white p-2 shadow-lg',
						variants({
							variant: {
								default: undefined,
								borderless: cn(
									'gap-0 border-borders-primary bg-bg-interactive-light-bg-active_default',
									'shadow-[0px_4px_7px_0px_rgba(25,25,25,0.05)]',
								),
							},
							inline: [
								'relative shadow-md',
								'absolute z-50 overflow-x-visible whitespace-nowrap',
							],
							compact: 'w-auto',
							fullWidth: 'w-full max-w-none',
						}),
					)}
					tabIndex={-1}
					onKeyDown={handleKeyDown}
				>
					{searchable && (
						<input
							type="text"
							className="w-full mb-2 px-2 py-1 border border-grey-300 rounded text-sm focus:outline-none"
							placeholder="Search..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							autoFocus
						/>
					)}

					{filteredOptions().length > 0 ? (
						filteredOptions().map((option, index) => (
							<button
								type="button"
								key={option.value}
								role="option"
								id={`option-${option.value}`}
								aria-selected={option.value === value}
								className={cn(
									'flex w-full cursor-pointer items-center gap-4 self-stretch border-0',
									'py-2.5 pl-3 pr-2 text-left text-sm leading-5 text-text-primary',
									'rounded-sm hover:bg-bg-interactive-light-bg-active_hover',
									index === activeIndex && 'bg-bg-interactive-light-bg-active_pressing!',
								)}
								onClick={() => {
									onChange(option.value);
									setIsOpen(false);
								}}
								tabIndex={-1}
								ref={(el) => {
									optionRefs.current[index] = el;
								}}
							>
								<div className="flex min-w-0 flex-1 items-center gap-2">
									{hasIcons && (
										<div className={iconSlot}>
											{option.icon}
										</div>
									)}
									<span className="min-w-0 flex-1 truncate">{option.label}</span>
								</div>
								{option.value === value && (
									<div className={iconSlot}>
										<Icon path={mdiCheck} size="20px" />
									</div>
								)}
							</button>
						))
					) : (
						<div className="px-3 py-2 text-sm text-grey-500 w-full">No results</div>
					)}
				</div>
			)}
		</div>
	);
}
